import { Globe, Mail } from 'lucide-react';
import { 
  FaTiktok, 
  FaYoutube, 
  FaInstagram, 
  FaFacebook, 
  FaLinkedin 
} from 'react-icons/fa';
import { SiX } from 'react-icons/si';
import React from 'react';

export type PlatformKey = 'youtube' | 'youtube-shorts' | 'tiktok' | 'instagram' | 'twitter' | 'facebook' | 'linkedin' | 'link' | 'newsletter';

export interface PlatformConfig {
  key: PlatformKey;
  label: string;
  icon: React.ComponentType<any>;
  color: string;
  aspectRatio: '16/9' | '9/16' | '4/5' | 'auto';
  build: (input: string) => {
    mode: 'iframe' | 'blockquote';
    html: string;
    scripts?: string[];
    process?: () => void;
    aspectRatio?: '16/9' | '9/16' | '4/5' | 'auto';
  } | null;
  buildAsync?: (input: string) => Promise<{
    mode: 'iframe' | 'blockquote';
    html: string;
    scripts?: string[];
    process?: () => void;
    aspectRatio?: '16/9' | '9/16' | '4/5' | 'auto';
  } | null>;
  /** OPTIONAL sizing hint used by UI containers */
  fit?: 'cover' | 'scroll';
}

function sanitizeUrl(input: string): string {
  // Remove script tags and other potentially dangerous content
  return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
}

function normalizeUrl(input: string): string {
  const sanitized = sanitizeUrl(input);
  
  // If it already has a protocol, return as is
  if (/^https?:\/\//i.test(sanitized)) {
    return sanitized;
  }
  
  // If it looks like a domain (contains dots and no spaces), add https://
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(sanitized) && !sanitized.includes(' ')) {
    return `https://${sanitized}`;
  }
  
  // For other cases, return as is (might be a relative path or invalid URL)
  return sanitized;
}


export const PLATFORM_CONFIGS: Record<PlatformKey, PlatformConfig> = {
  youtube: {
    key: 'youtube',
    label: 'YouTube',
    icon: FaYoutube,
    color: '#FF0000', // YouTube red
    aspectRatio: '16/9',
    fit: 'cover',
    build: (input: string) => {
      const u = sanitizeUrl(input);
      let videoId: string | null = null;
      let aspectRatio = '16/9';
      
      // Handle embed codes first
      if (u.includes('youtube.com/embed/')) {
        videoId = u.match(/embed\/([^?]+)/)?.[1] || null;
      } else if (u.includes('/watch?v=')) {
        videoId = u.match(/v=([^&]+)/)?.[1] || null;
      } else if (u.includes('/youtu.be/')) {
        videoId = u.match(/youtu\.be\/([^?]+)/)?.[1] || null;
      } else if (u.includes('/shorts/')) {
        videoId = u.match(/shorts\/([^?]+)/)?.[1] || null;
        aspectRatio = '9/16';
      } else if (u.includes('/playlist?list=')) {
        videoId = u.match(/list=([^&]+)/)?.[1] || null;
      }
      
      if (!videoId) {
        return null;
      }
      
      // Handle different YouTube content types
      let src: string;
      if (u.includes('/shorts/') || (u.includes('shorts') && !u.includes('embed'))) {
        src = `https://www.youtube.com/embed/${videoId}`;
        aspectRatio = '9/16';
      } else if (u.includes('/playlist?list=')) {
        src = `https://www.youtube.com/embed/videoseries?list=${videoId}`;
      } else {
        src = `https://www.youtube.com/embed/${videoId}`;
      }
      
      const html = `<iframe width="100%" height="100%" src="${src}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen data-platform="youtube"></iframe>`;
      return { mode: 'iframe', html, aspectRatio: aspectRatio as '16/9' | '9/16' };
    },
  },

  'youtube-shorts': {
    key: 'youtube-shorts',
    label: 'YouTube Shorts',
    icon: FaYoutube,
    color: '#FF0000', // YouTube red
    aspectRatio: '9/16',
    fit: 'cover',
    build: (input: string) => {
      const u = sanitizeUrl(input);
      let videoId: string | null = null;
      
      // Extract video ID from YouTube Shorts URL
      const shortsMatch = u.match(/(?:youtube\.com\/shorts\/|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (shortsMatch) {
        videoId = shortsMatch[1];
      }
      
      if (!videoId) {
        return null;
      }
      
      const src = `https://www.youtube.com/embed/${videoId}`;
      const html = `<iframe width="100%" height="100%" src="${src}" title="YouTube Shorts video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen data-platform="youtube-shorts"></iframe>`;
      return { mode: 'iframe', html, aspectRatio: '9/16' as const };
    },
  },

  tiktok: {
    key: 'tiktok',
    label: 'TikTok',
    icon: FaTiktok,
    color: '#000000', // TikTok black
    aspectRatio: '9/16',
    fit: 'cover',
    build: (input: string) => {
      const u = sanitizeUrl(input);
      const id = u.match(/\/video\/(\d+)/)?.[1] || u.match(/data-video-id="([^"]+)"/)?.[1] || null;
      if (!id) return null;
      
      // iframe v2 (no SDK hydration)
      const src = `https://www.tiktok.com/embed/v2/${id}`;
      const html = `<iframe src="${src}" allow="encrypted-media; clipboard-write" referrerpolicy="strict-origin-when-cross-origin" style="width:100%;height:100%;border:0" title="TikTok"></iframe>`;
      
      return { mode: 'iframe', html, aspectRatio: '9/16' as const };
    },
  },

  instagram: {
    key: 'instagram',
    label: 'Instagram',
    icon: FaInstagram,
    color: '#E4405F', // Instagram pink
    aspectRatio: 'auto',
    fit: 'scroll',
    build: (input: string) => {
      const u = sanitizeUrl(input);
      let cleanUrl = u;
      
      // Handle embed codes first
      if (u.includes('data-instgrm-permalink=')) {
        const urlMatch = u.match(/data-instgrm-permalink="([^"]+)"/);
        if (urlMatch) {
          cleanUrl = urlMatch[1];
        }
      } else if (u.includes('instagram.com/p/') || u.includes('instagram.com/reel/')) {
        // Direct URL
        cleanUrl = u;
      }
      
      // Clean URL by removing UTM parameters
      cleanUrl = cleanUrl.split('?')[0];
      
      const html = `<blockquote class="instagram-media" data-instgrm-captioned data-instgrm-permalink="${cleanUrl}" data-instgrm-version="14" style="background:#FFF; border:0; border-radius:3px; box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); margin: 1px; max-width:100%; min-width:100%; width:100%; padding:0;"></blockquote>`;
      return {
        mode: 'blockquote',
        html,
        scripts: ['https://www.instagram.com/embed.js'],
        process: () => {
          if ((window as any).instgrm?.Embeds?.process) {
            (window as any).instgrm.Embeds.process();
          } else {
            const checkInstgrm = setInterval(() => {
              if ((window as any).instgrm?.Embeds?.process) {
                (window as any).instgrm.Embeds.process();
                clearInterval(checkInstgrm);
              }
            }, 100);
            setTimeout(() => clearInterval(checkInstgrm), 5000);
          }
        },
      };
    },
  },

  twitter: {
    key: 'twitter',
    label: 'X (Twitter)',
    icon: SiX,
    color: '#000000', // X black (new branding)
    aspectRatio: 'auto',
    fit: 'scroll',
    build: (input: string) => {
      const u = sanitizeUrl(input).replace(/^@/, '');
      // Try to find a full tweet URL
      let tweetUrl = u;
      const m = u.match(/https?:\/\/(?:twitter\.com|x\.com)\/[^/]+\/status\/\d+/i);
      if (m) tweetUrl = m[0];

      // Canonical embed markup
      const html = `
        <blockquote class="twitter-tweet" data-dnt="true" style="width:100%;margin:0">
          <a href="${tweetUrl}"></a>
        </blockquote>
      `;

      return {
        mode: 'blockquote',
        html,
        scripts: ['https://platform.twitter.com/widgets.js'], // ensure script loads
        process: () => {
          (window as any).twttr?.widgets?.load?.();
        }
      };
    },
  },

  facebook: {
    key: 'facebook',
    label: 'Facebook',
    icon: FaFacebook,
    color: '#1877F2', // Facebook blue
    aspectRatio: 'auto',
    fit: 'cover',
    build: (input: string) => {
      const u = sanitizeUrl(input);
      let postUrl = u;
      let pluginType = 'post';
      
      // Handle embed codes first
      if (u.includes('data-href=')) {
        const urlMatch = u.match(/data-href="([^"]+)"/);
        if (urlMatch) {
          postUrl = urlMatch[1];
        }
      } else if (u.includes('facebook.com/plugins/')) {
        // Extract URL from iframe src
        const urlMatch = u.match(/href=([^&]+)/);
        if (urlMatch) {
          postUrl = decodeURIComponent(urlMatch[1]);
        }
      } else if (u.includes('facebook.com/') || u.includes('fb.com/')) {
        // Direct URL
        postUrl = u;
      }
      
      // Determine if it's a video/reel or regular post
      if (postUrl.includes('/reel/') || postUrl.includes('/video/')) {
        pluginType = 'video';
      }
      
      // Use iframe approach for better reliability
      const src = `https://www.facebook.com/plugins/${pluginType}.php?href=${encodeURIComponent(postUrl)}&width=500&show_text=true&height=589&appId`;
      const html = `<iframe src="${src}" width="500" height="589" style="border:none;overflow:hidden;width:100%;height:100%" scrolling="no" frameborder="0" allowfullscreen="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>`;
      
      return { 
        mode: 'iframe', 
        html
      };
    },
  },

  linkedin: {
    key: 'linkedin',
    label: 'LinkedIn',
    icon: FaLinkedin,
    color: '#0A66C2', // LinkedIn blue
    aspectRatio: 'auto',
    fit: 'cover',
    build: (input: string) => {
      const u = sanitizeUrl(input);
      let embedUrl = u;
      
      // Handle embed codes first
      if (u.includes('linkedin.com/embed/')) {
        embedUrl = u;
      } else if (u.includes('linkedin.com/posts/') || u.includes('linkedin.com/feed/update/')) {
        // Convert post URL to embed URL
        const postId = u.match(/(?:posts|feed\/update)\/([^?]+)/)?.[1];
        if (postId) {
          embedUrl = `https://www.linkedin.com/embed/feed/update/urn:li:share:${postId}`;
        }
      }
      
      const html = `<iframe src="${embedUrl}?collapsed=1" height="596" width="100%" frameborder="0" allowfullscreen="" title="Embedded post" style="width:100%;height:100%"></iframe>`;
      
      return { 
        mode: 'iframe', 
        html
      };
    },
  },

  link: {
    key: 'link',
    label: 'Link',
    icon: Globe,
    color: '#6B7280',
    aspectRatio: 'auto',
    fit: 'cover',
    build: (input: string) => {
      const u = normalizeUrl(input);
      return { 
        mode: 'iframe', 
        html: `<iframe src="${u}" width="100%" height="400" frameborder="0" style="width:100%;height:100%"></iframe>`
      };
    },
  },

  newsletter: {
    key: 'newsletter',
    label: 'Newsletter',
    icon: Mail,
    color: '#10B981',
    aspectRatio: 'auto',
    fit: 'cover',
    build: (input: string) => {
      const u = normalizeUrl(input);
      return { 
        mode: 'iframe', 
        html: `<iframe src="${u}" width="100%" height="400" frameborder="0" style="width:100%;height:100%"></iframe>`
      };
    },
  }
};

// Helper function to get platform config from URL
export function getPlatformConfig(url: string): PlatformConfig {
  // Enhanced platform detection with extended types
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    return PLATFORM_CONFIGS.youtube;
  }
  if (url.includes('tiktok.com')) {
    return PLATFORM_CONFIGS.tiktok;
  }
  if (url.includes('instagram.com')) {
    return PLATFORM_CONFIGS.instagram;
  }
  if (url.includes('twitter.com') || url.includes('x.com')) {
    return PLATFORM_CONFIGS.twitter;
  }
  if (url.includes('facebook.com')) {
    return PLATFORM_CONFIGS.facebook;
  }
  if (url.includes('linkedin.com')) {
    return PLATFORM_CONFIGS.linkedin;
  }
  
  // Default fallback
  return PLATFORM_CONFIGS.link;
}

// Helper function to map extended platform types to base platform keys
export function mapExtendedPlatformToBase(extendedPlatform: string): PlatformKey {
  if (extendedPlatform.includes('youtube')) return 'youtube';
  if (extendedPlatform.includes('instagram')) return 'instagram';
  if (extendedPlatform.includes('twitter')) return 'twitter';
  if (extendedPlatform.includes('facebook')) return 'facebook';
  if (extendedPlatform.includes('tiktok')) return 'tiktok';
  if (extendedPlatform.includes('linkedin')) return 'linkedin';
  if (extendedPlatform === 'link') return 'link';
  if (extendedPlatform === 'newsletter') return 'newsletter';
  
  // Default fallback
  return 'link';
} 